/**
 * BookingPage - Add class to cart with participant details
 */

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCheckout, formatBookingDate } from '../../context/CheckoutContext';
import {
  getEventDetails,
  getEventPricing,
  getEventAvailability,
  getPublicSystemSettings,
  formatPrice,
  CLASS_TIME_SLOTS,
  type TourStop,
  type EventPricingResponse,
  type ClassTimeSlot,
  type TimeSlotSelection,
  type TimeSlot,
  type DateViewMode,
  type EventAvailabilityResult,
  type PublicSystemSettings,
} from '../../api/events';
import { TimeSlotSelector } from '../../components/events';
import { SimultaneousSessionModal } from '../../components/events/SimultaneousSessionModal';
import { StartTimePreferenceSelector } from '../../components/events/StartTimePreferenceSelector';
import { isMatineeThresholdTriggered, resolveSessionTimes, formatTimeDisplay, addMinutes, type SessionMode } from '@sda/shared';
import { getStudioProfile, type Studio } from '../../api/studios';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { buildLocationParams } from '../../utils/location';

const LEVEL_LABELS: Record<string, string> = {
  type_a: 'Beginner',
  type_b: 'Intermediate (Ages 9-12)',
  type_c: 'Intermediate/Advanced (Ages 12+)',
};

export function formatLevelLabel(level: string): string {
  return LEVEL_LABELS[level] || level || 'All Levels';
}

export default function BookingPage() {
  const { tourStopId } = useParams<{ tourStopId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addItem, items, userZip, userLat, userLng, setUserLocation } = useCheckout();

  // URL params
  const initialClassId = searchParams.get('class') || '';
  const initialDate = searchParams.get('date') || '';
  // Parse slot from URL — can be 'both', a standard ClassTimeSlot, or a custom 12h key (Monday)
  const slotParam = searchParams.get('slot');
  // Custom 12h key pattern: e.g. "6:00 PM", "10:30 AM" — not 'both' and not a standard slot
  const isCustomSlotParam = !!slotParam && slotParam !== 'both' && !CLASS_TIME_SLOTS.includes(slotParam as ClassTimeSlot) && /^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(slotParam);
  const initialSlot: TimeSlotSelection | undefined =
    slotParam === 'both'
      ? 'both'
      : slotParam && CLASS_TIME_SLOTS.includes(slotParam as ClassTimeSlot)
        ? (slotParam as ClassTimeSlot)
        : isCustomSlotParam
          ? (slotParam as TimeSlotSelection)
          : undefined;
  const zipFromUrl = searchParams.get('zip') || '';
  const latFromUrl = searchParams.get('lat') || '';
  const lngFromUrl = searchParams.get('lng') || '';
  const qFromUrl = searchParams.get('q') || '';
  const level1FromUrl = searchParams.get('level1') || '';
  const level2FromUrl = searchParams.get('level2') || '';
  const prefFromUrl = searchParams.get('pref') as 'early' | 'late' | null;
  const simulFromUrl = searchParams.get('simul') === '1';
  const simulTriggerFromUrl = searchParams.get('simul_trigger') as 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null;

  // Determine if we have a valid location from URL (zip or lat/lng)
  const hasLocationFromUrl = !!zipFromUrl || (!!latFromUrl && !!lngFromUrl);

  // Build location query string for links
  const locationQuery = buildLocationParams({
    zip: zipFromUrl || userZip || undefined,
    lat: latFromUrl || (userLat != null ? String(userLat) : undefined),
    lng: lngFromUrl || (userLng != null ? String(userLng) : undefined),
    q: qFromUrl || undefined,
  });

  // State
  const [event, setEvent] = useState<TourStop | null>(null);
  const [pricing, setPricing] = useState<EventPricingResponse | null>(null);
  const [availability, setAvailability] = useState<TimeSlot[]>([]);
  const [dateViewMode, setDateViewMode] = useState<DateViewMode>('auto');
  const [systemSettings, setSystemSettings] = useState<PublicSystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Logged-in studio's profile — drives the "<studio name> - <city>, <state>"
  // subline on the booking header (SDA-155). Falls back to theater/city info
  // when not authenticated or while loading.
  const [studio, setStudio] = useState<Studio | null>(null);
  useEffect(() => {
    if (!isAuthenticated) {
      setStudio(null);
      return;
    }
    let cancelled = false;
    getStudioProfile()
      .then((s) => {
        if (!cancelled) setStudio(s);
      })
      .catch(() => {
        if (!cancelled) setStudio(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Form state
  const [selectedClassId, setSelectedClassId] = useState(initialClassId);
  // Date and time slot - from URL params or selected inline
  const [selectedDate, setSelectedDate] = useState<string | undefined>(initialDate || undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotSelection | undefined>(initialSlot);
  // Whether date/time need to be selected inline (no URL params)
  const needsDateTimeSelection = !initialDate || !initialSlot;
  // Track levels selected inline (or from URL params)
  const [selectedLevels, setSelectedLevels] = useState<{ session1?: string; session2?: string }>({
    session1: level1FromUrl || undefined,
    session2: level2FromUrl || undefined,
  });
  // Effective level values: state takes priority over URL params
  const effectiveLevel1 = selectedLevels.session1 || level1FromUrl;
  const effectiveLevel2 = selectedLevels.session2 || level2FromUrl;
  // When "both" is selected, we need separate levels for each session
  const isBothSessions = selectedTimeSlot === 'both';
  const [specialRequests, setSpecialRequests] = useState('');
  const [includeMarketing, setIncludeMarketing] = useState(false);
  const [showMarketingPreview, setShowMarketingPreview] = useState(false);
  const [includeGroupTickets, setIncludeGroupTickets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Session scheduling state (initialized from URL params when coming from EventDetailPage)
  const [startTimePreference, setStartTimePreference] = useState<'early' | 'default' | 'late'>(prefFromUrl || 'default');
  const [simultaneousSessions, setSimultaneousSessions] = useState(simulFromUrl);
  const [simultaneousTrigger, setSimultaneousTrigger] = useState<string | null>(simulTriggerFromUrl || null);
  const [showSimultaneousModal, setShowSimultaneousModal] = useState(false);
  const [pendingSimultaneousTrigger, setPendingSimultaneousTrigger] = useState<'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null>(null);

  // Compute matinee threshold in BookingPage (lifted from TimeSlotSelector)
  const isMatineeThresholdActive = (() => {
    if (!systemSettings) return false;
    try {
      return isMatineeThresholdTriggered(
        event?.matinee_time || null,
        systemSettings.matinee_threshold_time,
        systemSettings.default_matinee_time
      );
    } catch {
      return false;
    }
  })();

  // pricing.distance_miles is the user-zip-aware distance; event.straight_line_miles
  // is only populated on the search endpoint, not getEventDetails.
  const isDistanceThresholdTriggered = isBothSessions && (pricing?.distance_miles ?? 0) >= 40;
  const isSimultaneousTriggered = isMatineeThresholdActive || isDistanceThresholdTriggered;

  // Set location from URL params
  useEffect(() => {
    if (hasLocationFromUrl) {
      setUserLocation({
        zip: zipFromUrl || undefined,
        lat: latFromUrl ? parseFloat(latFromUrl) : undefined,
        lng: lngFromUrl ? parseFloat(lngFromUrl) : undefined,
      });
    }
  }, [zipFromUrl, latFromUrl, lngFromUrl, hasLocationFromUrl, setUserLocation]);

  // Load event details
  useEffect(() => {
    if (!tourStopId) return;

    const zip = zipFromUrl || userZip;
    const la = latFromUrl ? parseFloat(latFromUrl) : userLat;
    const ln = lngFromUrl ? parseFloat(lngFromUrl) : userLng;
    const hasLoc = !!zip || (la != null && ln != null);

    if (!hasLoc) {
      navigate(locationQuery ? `/events/${tourStopId}?${locationQuery}` : `/events/${tourStopId}`);
      return;
    }

    const location = {
      zip: zip || undefined,
      lat: la ?? undefined,
      lng: ln ?? undefined,
    };

    setLoading(true);
    setError(null);

    Promise.all([
      getEventDetails(tourStopId),
      getEventPricing(tourStopId, location),
      // Always fetch availability: even with date+slot prefilled from the URL we
      // need each slot's day_type/start_time to render Monday times correctly.
      // The Monday 9:00 AM slot shares the "9:00 AM" key with Saturday's Session 1,
      // so we can't tell it's a multi-slot day from the slot string alone.
      getEventAvailability(tourStopId),
      getPublicSystemSettings().catch(() => null),
    ])
      .then(([eventData, pricingData, availabilityResult, settingsData]) => {
        setEvent(eventData);
        setPricing(pricingData);
        setAvailability((availabilityResult as EventAvailabilityResult).slots);
        setDateViewMode((availabilityResult as EventAvailabilityResult).dateViewMode);
        if (settingsData) setSystemSettings(settingsData);
      })
      .catch((err) => {
        setError(getFriendlyErrorMessage(err, 'Failed to load event details'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tourStopId, zipFromUrl, latFromUrl, lngFromUrl, userZip, userLat, userLng, navigate]);

  // For Monday slots: find the exact availability slot by date + time_slot key to get start_time/end_time
  const selectedSlotData = (selectedDate && selectedTimeSlot && availability.length > 0)
    ? availability.find((s) => s.date === selectedDate && s.time_slot === selectedTimeSlot)
    : undefined;
  const isMondaySelection = selectedSlotData?.day_type === 'monday';

  // Get selected class details
  const selectedClass = pricing?.class_options.find((c) => c.id === selectedClassId);

  // Check if already in cart
  // For "both" - check if BOTH sessions are in cart
  const isAlreadyInCart = isBothSessions
    ? items.some(
        (item) =>
          item.tourStopId === tourStopId &&
          item.classOptionId === selectedClassId &&
          item.scheduledDate === selectedDate &&
          item.timeSlot === '9:00 AM'
      ) &&
      items.some(
        (item) =>
          item.tourStopId === tourStopId &&
          item.classOptionId === selectedClassId &&
          item.scheduledDate === selectedDate &&
          item.timeSlot === '11:00 AM'
      )
    : items.some(
        (item) =>
          item.tourStopId === tourStopId &&
          item.classOptionId === selectedClassId &&
          item.scheduledDate === selectedDate &&
          item.timeSlot === selectedTimeSlot
      );

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!event || !selectedClass || !selectedDate || !selectedTimeSlot) return;
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    setIsSubmitting(true);

    try {
      const effectiveSimultaneous = simultaneousSessions || isSimultaneousTriggered;
      const effectiveTrigger = isMatineeThresholdActive ? 'matinee_threshold' : isDistanceThresholdTriggered ? 'distance_threshold' : simultaneousTrigger;

      if (isBothSessions) {
        // Add TWO separate cart items - one for each session
        // Session 1: 9:00 AM
        addItem({
          tourStopId: event.id,
          classOptionId: selectedClass.id,
          className: selectedClass.name,
          showId: event.show.id,
          showName: event.show.name,
          showLogoUrl: event.show.logo_url,
          venueName: event.theater.name,
          venueCity: event.city.name,
          venueState: event.city.state,
          scheduledDate: selectedDate,
          sessionIdentifier: 'session_1',
          timeSlot: '9:00 AM',
          basePriceCents: selectedClass.base_price_cents,
          travelSurchargeCents: selectedClass.travel_surcharge_cents,
          totalPriceCents: selectedClass.total_price_cents,
          durationMinutes: selectedClass.duration_minutes,
          studentCount: null,
          studentLevel: effectiveLevel1 || null,
          specialRequests: specialRequests || null,
          includeMarketingMaterials: includeMarketing,
          groupTicketInquiry: includeGroupTickets,
          startTimePreference,
          simultaneousSessions: effectiveSimultaneous,
          simultaneousTrigger: effectiveTrigger,
        });

        // Session 2: 11:00 AM
        // Marketing materials only charged once (on Session 1), so always false here
        addItem({
          tourStopId: event.id,
          classOptionId: selectedClass.id,
          className: selectedClass.name,
          showId: event.show.id,
          showName: event.show.name,
          showLogoUrl: event.show.logo_url,
          venueName: event.theater.name,
          venueCity: event.city.name,
          venueState: event.city.state,
          scheduledDate: selectedDate,
          sessionIdentifier: 'session_2',
          timeSlot: '11:00 AM',
          basePriceCents: selectedClass.base_price_cents,
          travelSurchargeCents: selectedClass.travel_surcharge_cents,
          totalPriceCents: selectedClass.total_price_cents,
          durationMinutes: selectedClass.duration_minutes,
          studentCount: null,
          studentLevel: effectiveLevel2 || null,
          specialRequests: specialRequests || null,
          includeMarketingMaterials: false,
          groupTicketInquiry: false,
          startTimePreference,
          simultaneousSessions: effectiveSimultaneous,
          simultaneousTrigger: effectiveTrigger,
        });
      } else if (isMondaySelection && selectedSlotData) {
        // Monday multi-slot booking — exact time from availability, no session resolution
        addItem({
          tourStopId: event.id,
          classOptionId: selectedClass.id,
          className: selectedClass.name,
          showId: event.show.id,
          showName: event.show.name,
          showLogoUrl: event.show.logo_url,
          venueName: event.theater.name,
          venueCity: event.city.name,
          venueState: event.city.state,
          scheduledDate: selectedDate,
          sessionIdentifier: undefined,
          timeSlot: selectedTimeSlot as string,
          slotStartTime: selectedSlotData.start_time,
          slotEndTime: selectedSlotData.end_time,
          dayType: 'monday',
          basePriceCents: selectedClass.base_price_cents,
          travelSurchargeCents: selectedClass.travel_surcharge_cents,
          totalPriceCents: selectedClass.total_price_cents,
          durationMinutes: selectedClass.duration_minutes,
          studentCount: null,
          studentLevel: effectiveLevel1 || effectiveLevel2 || null,
          specialRequests: specialRequests || null,
          includeMarketingMaterials: includeMarketing,
          groupTicketInquiry: includeGroupTickets,
          // No startTimePreference for Monday — slot is exact
        });
      } else {
        // Standard Sat/Sun single session booking
        const sessionId = selectedTimeSlot === '9:00 AM' ? 'session_1' as const : 'session_2' as const;
        addItem({
          tourStopId: event.id,
          classOptionId: selectedClass.id,
          className: selectedClass.name,
          showId: event.show.id,
          showName: event.show.name,
          showLogoUrl: event.show.logo_url,
          venueName: event.theater.name,
          venueCity: event.city.name,
          venueState: event.city.state,
          scheduledDate: selectedDate,
          sessionIdentifier: sessionId,
          timeSlot: selectedTimeSlot as string,
          dayType: selectedSlotData?.day_type ?? undefined,
          basePriceCents: selectedClass.base_price_cents,
          travelSurchargeCents: selectedClass.travel_surcharge_cents,
          totalPriceCents: selectedClass.total_price_cents,
          durationMinutes: selectedClass.duration_minutes,
          studentCount: null,
          studentLevel: effectiveLevel1 || effectiveLevel2 || null,
          specialRequests: specialRequests || null,
          includeMarketingMaterials: includeMarketing,
          groupTicketInquiry: includeGroupTickets,
          startTimePreference,
        });
      }

      navigate('/checkout');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-warm-200 rounded w-1/2" />
            <div className="h-4 bg-warm-200 rounded w-1/3" />
            <div className="card">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-warm-100 rounded" />
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !event || !pricing) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-warm-900 mb-2">{error || 'Event not found'}</h2>
            <Link to="/events" className="btn-primary mt-4">
              Browse Events
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50">
      <Header cartCount={items.length} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-warm-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-warm-500">
            <Link to="/" className="hover:text-warm-700">Home</Link>
            <span>/</span>
            <Link to={`/events?${locationQuery}`} className="hover:text-warm-700">Events</Link>
            <span>/</span>
            <Link to={`/events/${tourStopId}?${locationQuery}`} className="hover:text-warm-700">
              {event.show.name}
            </Link>
            <span>/</span>
            <span className="text-warm-900">Book Class</span>
          </nav>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Header */}
        <div className="flex items-start gap-4 mb-8">
          {event.show.logo_url && (
            <img
              src={event.show.logo_url}
              alt={event.show.name}
              className="w-16 h-16 rounded-lg object-cover shadow"
            />
          )}
          <div>
            {/* SDA-155: framed as "Master Class w/ cast member from <show>".
                Today there's one show per tour stop; if multi-show events
                become a thing, switch to a comma-joined list. */}
            <h1 className="text-2xl font-display font-bold text-warm-900">
              Master Class w/ cast member from {event.show.name}
            </h1>
            <p className="text-warm-600">
              {studio && studio.city && studio.state
                ? `${studio.name} - ${studio.city}, ${studio.state}`
                : `${event.theater.name} · ${event.city.name}, ${event.city.state}`}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Class Selection */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Select Class</h2>
              <div className="space-y-3">
                {pricing.class_options.map((option) => (
                  <label
                    key={option.id}
                    className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedClassId === option.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-warm-200 hover:border-warm-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="classOption"
                          value={option.id}
                          checked={selectedClassId === option.id}
                          onChange={(e) => setSelectedClassId(e.target.value)}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <div className="font-medium text-warm-900">{option.name}</div>
                          <div className="text-sm text-warm-500">
                            {option.duration_minutes} minutes · Up to {option.max_students} students
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-warm-900">
                          {formatPrice(option.total_price_cents)}
                        </div>
                        {option.travel_surcharge_cents > 0 && (
                          <div className="text-xs text-warm-500">
                            includes {formatPrice(option.travel_surcharge_cents)} travel
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Date & Time Selection */}
            {needsDateTimeSelection ? (
              <div className="card">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">Select Date & Time</h2>
                {availability.length > 0 ? (
                  <>
                    <TimeSlotSelector
                      availability={availability}
                      selectedDate={selectedDate}
                      selectedTimeSlot={selectedTimeSlot}
                      selectedLevels={selectedLevels}
                      onSelect={(date, timeSlot, levels) => {
                        setSelectedDate(date);
                        setSelectedTimeSlot(timeSlot);
                        setSelectedLevels(levels || {});
                        // Reset simultaneous state when changing selection
                        if (timeSlot !== 'both') {
                          setSimultaneousSessions(false);
                          setSimultaneousTrigger(null);
                        }
                      }}
                      dateViewMode={dateViewMode}
                      systemSettings={systemSettings}
                      matineeTime={event?.matinee_time || null}
                      startTimePreference={startTimePreference}
                      forceSimultaneous={simultaneousSessions || isSimultaneousTriggered}
                      isSimultaneousTriggered={isSimultaneousTriggered}
                    />
                    {/* SDA-96: simultaneous-sessions checkbox lives INSIDE this
                        same card so it sits visually adjacent to the Both
                        Sessions selection. Previously rendered as a separate
                        sibling card below, which Ryan reported felt too far. */}
                    {isBothSessions && !isSimultaneousTriggered && systemSettings && (
                      <label className="flex items-start gap-3 cursor-pointer mt-4 pt-4 border-t border-warm-200">
                        <input
                          type="checkbox"
                          checked={simultaneousSessions}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPendingSimultaneousTrigger('manual_checkbox');
                              setShowSimultaneousModal(true);
                            } else {
                              setSimultaneousSessions(false);
                              setSimultaneousTrigger(null);
                            }
                          }}
                          className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <div>
                          <span className="font-medium text-warm-900">I would like both sessions running simultaneously</span>
                          <p className="text-sm text-warm-500 mt-0.5">This requires two separate studios available at the same time.</p>
                        </div>
                      </label>
                    )}
                  </>
                ) : (
                  <p className="text-warm-500 text-center py-4">
                    No available dates found for this event.
                  </p>
                )}
              </div>
            ) : selectedDate && selectedTimeSlot ? (
              <div className="card">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">Date & Time</h2>
                <div className="flex items-center justify-between p-4 bg-warm-50 rounded-lg">
                  <div>
                    <div className="font-medium text-warm-900">
                      {formatBookingDate(selectedDate)}
                    </div>
                    <div className="text-sm text-warm-600">
                      {isMondaySelection && selectedSlotData?.start_time ? (() => {
                        // Monday: show exact slot times from availability
                        const endTime = selectedSlotData.end_time
                          ?? (systemSettings ? addMinutes(selectedSlotData.start_time, systemSettings.session_duration_minutes) : undefined);
                        return `${formatTimeDisplay(selectedSlotData.start_time)}${endTime ? ` – ${formatTimeDisplay(endTime)}` : ''}`;
                      })() : isBothSessions && systemSettings ? (() => {
                        const bothMode: SessionMode = (simultaneousSessions || isSimultaneousTriggered) ? 'simultaneous' : 'back_to_back';
                        const resolved = resolveSessionTimes(
                          {
                            defaultSessionStartTime: systemSettings.default_session_start_time,
                            matineeTime: event?.matinee_time || null,
                            sessionDurationMinutes: systemSettings.session_duration_minutes,
                            matineeThresholdTime: systemSettings.matinee_threshold_time,
                            defaultMatineeTime: systemSettings.default_matinee_time,
                          },
                          startTimePreference,
                          bothMode,
                        );
                        const s1 = `${formatTimeDisplay(resolved.session1.start)} - ${formatTimeDisplay(resolved.session1.end)}`;
                        const s2 = `${formatTimeDisplay(resolved.session2.start)} - ${formatTimeDisplay(resolved.session2.end)}`;
                        return (
                          <span className="text-primary-600 font-medium">
                            {resolved.isSimultaneous
                              ? `Both Sessions: ${s1}`
                              : `Session 1: ${s1} & Session 2: ${s2}`}
                          </span>
                        );
                      })() : systemSettings ? (() => {
                        const singleMode: SessionMode = selectedTimeSlot === '11:00 AM' ? 'second_slot' : 'single';
                        const resolved = resolveSessionTimes(
                          {
                            defaultSessionStartTime: systemSettings.default_session_start_time,
                            matineeTime: event?.matinee_time || null,
                            sessionDurationMinutes: systemSettings.session_duration_minutes,
                            matineeThresholdTime: systemSettings.matinee_threshold_time,
                            defaultMatineeTime: systemSettings.default_matinee_time,
                          },
                          startTimePreference,
                          singleMode,
                        );
                        return `${formatTimeDisplay(resolved.session1.start)} – ${formatTimeDisplay(resolved.session1.end)}`;
                      })() : selectedTimeSlot}
                    </div>
                  </div>
                  <Link
                    to={`/events/${tourStopId}?${locationQuery}`}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Change
                  </Link>
                </div>
              </div>
            ) : null}

            {/* Session Scheduling — interactive fallback for direct navigation.
                Note: the manual-toggle simultaneous checkbox used to live here as
                its own card; SDA-96 moved it up into the time-selector card so it
                sits adjacent to the Both Sessions choice. The 40mi auto-required
                banner stays below, alongside the start-time preference.
                Hidden for Monday selections — slot is exact, preference N/A. */}
            {needsDateTimeSelection && selectedTimeSlot && systemSettings && !isMondaySelection && (
              <>
                {isDistanceThresholdTriggered && (
                  <div className="card border-2 border-amber-200 bg-amber-50">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <div className="font-medium text-warm-900">Both sessions will run simultaneously</div>
                        <p className="text-sm text-warm-700 mt-1">
                          Because this venue is over 40 miles from your studio, both sessions must run at the same time so the cast can make the show. You'll need two separate studios available simultaneously. Our team will confirm this with you after booking.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <StartTimePreferenceSelector
                  systemSettings={systemSettings}
                  matineeTime={event?.matinee_time || null}
                  preference={startTimePreference}
                  onPreferenceChange={setStartTimePreference}
                  isSimultaneous={simultaneousSessions || isSimultaneousTriggered}
                  selectedTimeSlot={selectedTimeSlot}
                />
              </>
            )}

            {/* Class Details */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Class Details</h2>

              <div className="space-y-3">
                {isBothSessions ? (() => {
                  const bothMode2: SessionMode = (simultaneousSessions || isSimultaneousTriggered) ? 'simultaneous' : 'back_to_back';
                  const times = systemSettings ? resolveSessionTimes(
                    {
                      defaultSessionStartTime: systemSettings.default_session_start_time,
                      matineeTime: event?.matinee_time || null,
                      sessionDurationMinutes: systemSettings.session_duration_minutes,
                      matineeThresholdTime: systemSettings.matinee_threshold_time,
                      defaultMatineeTime: systemSettings.default_matinee_time,
                    },
                    startTimePreference,
                    bothMode2,
                  ) : null;
                  const s1Time = times ? formatTimeDisplay(times.session1.start) : '9:00 AM';
                  const s2Time = times ? formatTimeDisplay(times.session2.start) : '11:00 AM';
                  return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium text-warm-800">Session 1 ({s1Time})</div>
                        <div className="text-sm text-warm-600">{formatLevelLabel(effectiveLevel1)}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium text-warm-800">Session 2 ({s2Time})</div>
                        <div className="text-sm text-warm-600">{formatLevelLabel(effectiveLevel2)}</div>
                      </div>
                    </div>
                  </div>
                  );
                })() : (
                  <div className="p-3 bg-warm-50 rounded-lg">
                    <div className="text-sm text-warm-600">Level: {formatLevelLabel(effectiveLevel1 || effectiveLevel2)}</div>
                  </div>
                )}

                <p className="text-sm text-warm-500">Up to 40 students per class</p>

                <div>
                  <label htmlFor="specialRequests" className="block text-sm font-medium text-warm-700 mb-1">
                    Special Requests or Notes {isBothSessions && '(applies to both sessions)'}
                  </label>
                  <textarea
                    id="specialRequests"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any special requests, accommodations, or information about your students..."
                    rows={3}
                    className="input w-full"
                    maxLength={500}
                  />
                  <p className="mt-1 text-xs text-warm-400">{specialRequests.length}/500 characters</p>
                </div>
              </div>
            </div>

            {/* Marketing Materials Add-on */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Marketing Materials</h2>
              <p className="text-sm text-warm-500 mb-2">
                Includes professional photos, class posters, and social media assets to promote your masterclass.
              </p>
              <button
                type="button"
                onClick={() => setShowMarketingPreview(true)}
                className="text-primary-600 hover:text-primary-700 underline text-sm mb-4 inline-block"
              >
                Click here to see an example
              </button>

              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeMarketing}
                  onChange={(e) => setIncludeMarketing(e.target.checked)}
                  className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-warm-900">
                      Marketing Materials Package
                      {isBothSessions && <span className="text-sm text-warm-500 ml-1">(covers both sessions)</span>}
                    </span>
                    <span className="font-semibold text-warm-900">+$50</span>
                  </div>
                </div>
              </label>
            </div>

            {/* Group Show Tickets */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Group Show Tickets</h2>
              <div className="text-sm text-warm-500 space-y-3 mb-4">
                <p>
                  We can coordinate a group ticket order for your dancers and studio families to see the show! There is a flat rate, $10 convenience fee added to each ticket.
                </p>
                <p>
                  As a group ticket organizer, SDA often secures discounted rates and reduced fees that make tickets less expensive than purchasing directly from the box office. If you would like us to handle the details and coordination, we are happy to take care of it for you.
                </p>
              </div>

              <label className="flex items-start gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeGroupTickets}
                  onChange={(e) => setIncludeGroupTickets(e.target.checked)}
                  className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-warm-900">Group Ticket Inquiry</span>
                    <span className="text-sm text-warm-500 italic">No additional cost to you</span>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Sidebar Summary */}
          <div className="lg:col-span-2">
            <div className="card sticky top-24">
              <h3 className="font-semibold text-warm-900 mb-4">Booking Summary</h3>

              {selectedClass ? (
                <div className="space-y-4">
                  {isBothSessions ? (
                    <>
                      {/* Both Sessions Summary */}
                      <div className="space-y-2 text-sm">
                        <div className="text-xs font-medium text-primary-600 uppercase tracking-wide mb-2">
                          2 Classes
                        </div>
                        <div className="flex justify-between">
                          <span className="text-warm-600">{selectedClass.name} × 2</span>
                          <span className="text-warm-900">{formatPrice(selectedClass.base_price_cents * 2)}</span>
                        </div>
                        {selectedClass.travel_surcharge_cents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-warm-600">Travel Surcharge × 2</span>
                            <span className="text-warm-900">
                              {formatPrice(selectedClass.travel_surcharge_cents * 2)}
                            </span>
                          </div>
                        )}
                        {includeMarketing && (
                          <div className="flex justify-between">
                            <span className="text-warm-600">Marketing Materials</span>
                            <span className="text-warm-900">$50</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-warm-200 pt-4">
                        <div className="flex justify-between font-semibold text-lg">
                          <span className="text-warm-900">Total</span>
                          <span className="text-warm-900">
                            {formatPrice(
                              (selectedClass.total_price_cents * 2) +
                              (includeMarketing ? 5000 : 0)
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-warm-500 mt-1">
                          {selectedDate && new Date(selectedDate) <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
                            ? 'Full payment due at checkout — class is within 15 days'
                            : <>{formatPrice(20000 + (includeMarketing ? 5000 : 0))} deposit due at checkout ($100/class{includeMarketing ? ' + marketing' : ''})</>
                          }
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Single Session Summary */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-warm-600">{selectedClass.name}</span>
                          <span className="text-warm-900">{formatPrice(selectedClass.base_price_cents)}</span>
                        </div>
                        {selectedClass.travel_surcharge_cents > 0 && (
                          <div className="flex justify-between">
                            <span className="text-warm-600">Travel Surcharge</span>
                            <span className="text-warm-900">
                              {formatPrice(selectedClass.travel_surcharge_cents)}
                            </span>
                          </div>
                        )}
                        {includeMarketing && (
                          <div className="flex justify-between">
                            <span className="text-warm-600">Marketing Materials</span>
                            <span className="text-warm-900">$50</span>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-warm-200 pt-4">
                        <div className="flex justify-between font-semibold text-lg">
                          <span className="text-warm-900">Total</span>
                          <span className="text-warm-900">
                            {formatPrice(
                              selectedClass.total_price_cents + (includeMarketing ? 5000 : 0)
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-warm-500 mt-1">
                          {selectedDate && new Date(selectedDate) <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
                            ? 'Full payment due at checkout — class is within 15 days'
                            : <>{formatPrice(10000 + (includeMarketing ? 5000 : 0))} deposit due at checkout ($100/class{includeMarketing ? ' + marketing' : ''})</>
                          }
                        </p>
                      </div>
                    </>
                  )}

                  <button
                    onClick={handleAddToCart}
                    disabled={!selectedDate || !selectedTimeSlot || isSubmitting || isAlreadyInCart}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4\" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Adding...
                      </span>
                    ) : isAlreadyInCart ? (
                      'Already in Cart'
                    ) : !selectedDate || !selectedTimeSlot ? (
                      'Select Date & Time'
                    ) : (
                      'Add to Cart'
                    )}
                  </button>

                  {items.length > 0 && (
                    <Link
                      to="/checkout"
                      className="block text-center text-primary-600 hover:text-primary-700 text-sm font-medium mt-2"
                    >
                      View Cart ({items.length} {items.length === 1 ? 'class' : 'classes'})
                    </Link>
                  )}
                </div>
              ) : (
                <p className="text-warm-500 text-center py-4">Select a class to continue</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Marketing Materials Preview Modal */}
      {showMarketingPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowMarketingPreview(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-warm-200">
              <h3 className="text-lg font-semibold text-warm-900">Marketing Materials Example</h3>
              <button
                type="button"
                onClick={() => setShowMarketingPreview(false)}
                className="p-1 rounded-lg hover:bg-warm-100 transition-colors text-warm-500 hover:text-warm-700"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <img
                src="/marketing-example-1.jpg"
                alt="Marketing materials example — The Outsiders"
                className="w-full rounded-lg"
              />
              <img
                src="/marketing-example-2.jpg"
                alt="Marketing materials example — The Great Gatsby"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Simultaneous Sessions Confirmation Modal */}
      {showSimultaneousModal && pendingSimultaneousTrigger && (
        <SimultaneousSessionModal
          trigger={pendingSimultaneousTrigger}
          cityName={event?.city.name}
          onConfirm={() => {
            setSimultaneousSessions(true);
            setSimultaneousTrigger(pendingSimultaneousTrigger);
            setShowSimultaneousModal(false);
            setPendingSimultaneousTrigger(null);
          }}
          onCancel={() => {
            setShowSimultaneousModal(false);
            setPendingSimultaneousTrigger(null);
            // If matinee threshold triggered the modal, deselect second session
            if (pendingSimultaneousTrigger === 'matinee_threshold') {
              setSelectedTimeSlot(selectedTimeSlot === 'both' ? '9:00 AM' : selectedTimeSlot);
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Header component with cart indicator
 */
function Header({ cartCount = 0 }: { cartCount?: number }) {
  const { isAuthenticated, user, logout } = useAuth();

  return (
    <header className="bg-white border-b border-warm-200 sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>

          <div className="flex items-center gap-4">
            {cartCount > 0 && (
              <Link
                to="/checkout"
                className="relative text-warm-600 hover:text-warm-900"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              </Link>
            )}

            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                  Dashboard
                </Link>
                <span className="text-warm-400 text-sm hidden sm:block">{user?.email}</span>
                <button
                  onClick={() => logout()}
                  className="text-warm-600 hover:text-warm-900 text-sm"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-warm-600 hover:text-warm-900">Sign In</Link>
                <Link to="/register" className="btn-primary">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
